import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { StorefrontLayout } from '../../components/storefront/StorefrontLayout';
import { DeadlineBanner } from '../../components/storefront/DeadlineBanner';
import { CountdownTimer } from '../../components/storefront/CountdownTimer';
import { ProductCard } from '../../components/storefront/ProductCard';
import { PreviewBanner } from '../../components/storefront/PreviewBanner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { mockStores } from '../../utils/mockData';
import { mockProducts } from '../../utils/mockProducts';
import { calculateStoreStatus } from '../../utils/storeStatus';
import { StoreStatus } from '../../types/store';
import { Clock, Lock, CheckCircle } from 'lucide-react';
export function Storefront() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const store = mockStores.find(s => s.slug === slug);
  // Check if in preview mode
  const isPreview = searchParams.get('preview') === '1';
  const forcedState = searchParams.get('state') as 'coming-soon' | 'open' | 'closed' | null;
  if (!store) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-50" data-id="element-1367">
        <div className="text-center" data-id="element-1368">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1369">
            Store Not Found
          </h1>
          <p className="text-gray-600 mb-4" data-id="element-1370">
            The store you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/')} data-id="element-1371">Go to Dashboard</Button>
        </div>
      </div>;
  }
  // Check if store is published (unless in preview mode)
  if (!store.settings.isPublished && !isPreview) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-50" data-id="element-1372">
        <div className="text-center max-w-md" data-id="element-1373">
          <Lock className="h-16 w-16 text-gray-400 mx-auto mb-4" data-id="element-1374" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1375">
            Store Not Available
          </h1>
          <p className="text-gray-600" data-id="element-1376">
            This store is not currently published.
          </p>
        </div>
      </div>;
  }
  const actualStatus = calculateStoreStatus(store);
  // Determine display status (forced state overrides actual status in preview)
  let displayStatus: StoreStatus = actualStatus;
  if (isPreview && forcedState) {
    switch (forcedState) {
      case 'coming-soon':
        displayStatus = 'scheduled';
        break;
      case 'open':
        displayStatus = 'open';
        break;
      case 'closed':
        displayStatus = 'closed';
        break;
    }
  }
  const products = mockProducts;
  const handleClosePreview = () => {
    // Remove preview params and reload
    navigate(`/store/${slug}`);
  };
  const renderComingSoon = () => <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-id="element-1377">
      <div className="mb-8" data-id="element-1378">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6" data-id="element-1379">
          <Clock className="h-10 w-10 text-blue-600" data-id="element-1380" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1381">
          Ordering Opens Soon
        </h1>
        <p className="text-xl text-gray-600 mb-8" data-id="element-1382">
          {store.messaging.comingSoonMessage}
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-8 mb-8" data-id="element-1383">
        <p className="text-sm text-gray-500 mb-2" data-id="element-1384">Store Opens</p>
        <p className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1385">
          {new Date(store.settings.openDate).toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short'
        })}
        </p>
        <CountdownTimer targetDate={store.settings.openDate} label="Opens in" size="lg" data-id="element-1386" />
      </div>

      <div className="space-y-4" data-id="element-1387">
        <Input type="email" placeholder="Enter your email to get notified" className="max-w-md mx-auto" data-id="element-1388" />
        <Button disabled className="opacity-50" data-id="element-1389">
          <Lock className="h-4 w-4 mr-2" data-id="element-1390" />
          Ordering Not Yet Available
        </Button>
      </div>
    </div>;
  const renderOpen = () => <>
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12" data-id="element-1391">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1392">
          <h1 className="text-4xl font-bold mb-2" data-id="element-1393">{store.name}</h1>
          <p className="text-xl opacity-90" data-id="element-1394">
            {store.messaging.openWindowMessage}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1395">
        <div className="mb-8" data-id="element-1396">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1397">
            Shop Team Gear
          </h2>
          <p className="text-gray-600" data-id="element-1398">
            All orders will be {store.messaging.estimatedTimeline.toLowerCase()}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1399">
          {products.map(product => <ProductCard key={product.id} product={product} storeSlug={store.slug} data-id="element-1400" />)}
        </div>

        {products.length === 0 && <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-1401">
            <p className="text-gray-500" data-id="element-1402">No products available yet.</p>
          </div>}
      </div>
    </>;
  const renderClosed = () => <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-id="element-1403">
      <div className="mb-8" data-id="element-1404">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6" data-id="element-1405">
          <Lock className="h-10 w-10 text-red-600" data-id="element-1406" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1407">
          This Store Is Closed
        </h1>
        <p className="text-xl text-gray-600 mb-8" data-id="element-1408">
          {store.messaging.closedMessage}
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-8 mb-8" data-id="element-1409">
        <div className="flex items-center justify-center space-x-2 text-gray-600 mb-4" data-id="element-1410">
          <CheckCircle className="h-5 w-5" data-id="element-1411" />
          <span data-id="element-1412">Already placed an order?</span>
        </div>
        <p className="text-sm text-gray-500" data-id="element-1413">
          Check your email for order confirmation and updates.
        </p>
      </div>

      <div className="space-y-4" data-id="element-1414">
        <p className="text-sm text-gray-500" data-id="element-1415">Questions about your order?</p>
        <Button variant="outline" data-id="element-1416">Contact Support</Button>
      </div>
    </div>;
  return <>
      {isPreview && <PreviewBanner forcedState={forcedState} onClose={handleClosePreview} data-id="element-1417" />}

      <StorefrontLayout storeName={store.name} storeSlug={store.slug} organizationName={store.organizationName} supportEmail={store.supportEmail} supportPhone={store.supportPhone} data-id="element-1418">
        {(displayStatus === 'open' || displayStatus === 'closing-soon') && <DeadlineBanner closeDate={store.settings.closeDate} isClosingSoon={displayStatus === 'closing-soon'} data-id="element-1419" />}

        {displayStatus === 'scheduled' && renderComingSoon()}
        {(displayStatus === 'open' || displayStatus === 'closing-soon') && renderOpen()}
        {displayStatus === 'closed' && renderClosed()}
      </StorefrontLayout>
    </>;
}